import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';

const STORAGE_KEY = 'tf_cookie_consent';

const applyConsent = (choice) => {
  // PostHog (mesure d'audience) — chargé par la plateforme. Opt-in/out selon le choix.
  try {
    if (window.posthog) {
      if (choice === 'accepted') window.posthog.opt_in_capturing?.();
      else window.posthog.opt_out_capturing?.();
    }
  } catch (e) { /* noop */ }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'accepted' || saved === 'refused') {
      applyConsent(saved);
    } else {
      // Par défaut : pas de mesure d'audience tant qu'aucun choix n'est fait.
      applyConsent('refused');
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const choose = (choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    applyConsent(choice);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[90] px-3 pb-3 sm:px-6 sm:pb-6"
          data-testid="cookie-consent"
          role="dialog"
          aria-label="Consentement aux cookies"
        >
          <div className="clip-frame mx-auto flex max-w-4xl flex-col gap-4 border border-neon/25 bg-panel/95 p-5 backdrop-blur-md sm:flex-row sm:items-center sm:gap-6 sm:p-6">
            <span className="clip-frame-sm hidden h-11 w-11 shrink-0 items-center justify-center border border-neon/30 bg-void/60 text-neon sm:flex">
              <Cookie size={18} />
            </span>
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon">Cookies & confidentialité</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-300">
                Nous utilisons des cookies essentiels au fonctionnement du site (panier, session) et, avec votre accord,
                des cookies de mesure d'audience pour l'améliorer. Détails dans notre{' '}
                <Link to="/confidentialite" className="text-neon underline-offset-2 hover:underline" data-testid="cookie-privacy-link">Politique de confidentialité</Link>.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => choose('refused')}
                data-testid="cookie-refuse"
                className="clip-frame-sm flex-1 border border-white/15 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-slate-300 transition-colors hover:border-white/40 hover:text-white sm:flex-none"
              >
                Refuser
              </button>
              <button
                onClick={() => choose('accepted')}
                data-testid="cookie-accept"
                className="clip-frame-sm flex-1 bg-neon px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-void transition-colors hover:bg-cyber sm:flex-none"
              >
                Accepter
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
